import { useEffect, useCallback } from 'react';
import { Platform, Alert } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import api from '../services/api';
import DeviceInfo from 'react-native-device-info';

/**
 * useInAppUpdate hook handles both Native Store updates (Google Play)
 * and can be extended for OTA (Expo Updates) in the future.
 * 
 * It ensures users are always on the correct version.
 */
export const useInAppUpdate = () => {
  const checkUpdate = useCallback(async () => {
    // Only applicable to Android (Play Store) in this implementation
    if (Platform.OS !== 'android') return;

    try {
      const SpInAppUpdates = require('sp-react-native-in-app-updates').default;
      const { IAUUpdateKind, IAUInstallStatus } = require('sp-react-native-in-app-updates');

      const inAppUpdates = new SpInAppUpdates(false); // false = use real Play Store

      // 1. Fetch our own version policy from the backend
      const versionResponse = await api.get('/version');
      const versionPolicy = versionResponse.data;
      const currentVersion = DeviceInfo.getVersion();

      console.log(`[InAppUpdate] Current version: ${currentVersion}, Policy:`, versionPolicy);

      // 2. Check Play Store for updates
      const result = await inAppUpdates.checkNeedsUpdate();
      console.log('[InAppUpdate] Play Store check result:', JSON.stringify(result));

      if (!result?.shouldUpdate) {
        console.log('[InAppUpdate] App is up to date on Play Store.');
        return;
      }

      // 3. Determine update type
      // If our backend says it's mandatory, or the version gap is too large, use IMMEDIATE
      const isMandatory = versionPolicy?.mandatory || false;

      if (isMandatory) {
        console.log('[InAppUpdate] Starting MANDATORY (IMMEDIATE) update...');
        await inAppUpdates.startUpdate({ 
          updateType: IAUUpdateKind.IMMEDIATE 
        });
      } else {
        console.log('[InAppUpdate] Starting OPTIONAL (FLEXIBLE) background update...');
        
        // Listen for download completion
        inAppUpdates.addStatusUpdateListener((statusData) => {
          if (statusData.status === IAUInstallStatus.DOWNLOADED) {
            console.log('[InAppUpdate] Flexible update downloaded! Silently marking for next launch.');
            
            // Instead of interrupting the user now, we can prompt them or just
            // wait for the next time they launch the app.
            // For a better UX, we'll show a small toast or non-blocking alert.
            Alert.alert(
              '✨ Update Downloaded',
              'A new version is ready. Would you like to restart now to apply it?',
              [
                { text: 'Later', style: 'cancel' },
                { 
                  text: 'Restart', 
                  onPress: () => inAppUpdates.installUpdate() 
                }
              ]
            );
          }
          
          if (statusData.status === IAUInstallStatus.INSTALLED || statusData.status === IAUInstallStatus.FAILED) {
            inAppUpdates.removeStatusUpdateListener(statusData);
          }
        });

        await inAppUpdates.startUpdate({ 
          updateType: IAUUpdateKind.FLEXIBLE 
        });
      }

    } catch (error) {
      console.warn('[InAppUpdate] Update check failed silently:', error?.message || error);
    }
  }, []);

  useEffect(() => {
    if (Platform.OS !== 'android') return;

    // Check on launch after a small delay
    const timer = setTimeout(() => {
      checkUpdate();
    }, 5000);

    return () => clearTimeout(timer);
  }, [checkUpdate]);
};
